import React from 'react'
import { User } from '../../../providers/context'
import { useTranslation } from '../../../providers/translation'
import { UserSelector } from '../../../components/userSelector/userSelector'
import { makeRequest } from '../../../request'
import { ComposeState, Recipient, SingleStudentRecipient, StudentGroupRecipient } from './composeTypes'
import { CustomRecipientMaker } from '../../../components/userSelector/customRecipientMaker'
import assert from 'assert'
import {
  loadRecipientsByIdentifiers,
  loadStudentsByIdentifiers,
} from '../../../components/userSelector/userSelectorPlugins'
import { Toasts } from '../../../providers/toast'
import { inputSingleFile } from '../../../components/media/fileUpload'
import { IconFileUpload } from '../../../icons/library'
import { Popups } from '../../../providers/popups'

interface RecipientSelectorProps {
  limitRecipients: boolean
  highlightReasonsWeCantSend: boolean
  composeState: ComposeState
  setSelectedRecipients: React.Dispatch<React.SetStateAction<Recipient[]>>
  user: User
  loadedUsers: Recipient[]
}

export const RecipientSelector: React.FC<RecipientSelectorProps> = ({
  composeState,
  setSelectedRecipients,
  user,
  limitRecipients,
  highlightReasonsWeCantSend,
  loadedUsers,
}) => {
  const { tr } = useTranslation()
  const isTeacher = user.type === 'teacher'

  const userDisplayConfig = React.useCallback(
    (r: Recipient) => {
      if (
        user.type === 'guardian' &&
        'schoolIDs' in r && // only teacherrecipients have schoolIDs
        r.schoolIDs.length > 0 && // only grey out if the recipient has a school
        composeState.regardingStudent &&
        composeState.regardingStudent.schoolIDs.length > 0 &&
        user.children.length > 1 && // only grey out if there are multiple children
        !r.schoolIDs.some((id) => composeState.regardingStudent!.schoolIDs.includes(id))
      )
        return {
          disabled: true,
          tooltip: 'The selected student is not at the same school as this teacher. Please select a different student.',
        }
      return undefined
    },
    [composeState.regardingStudent]
  )

  const userVisibleFilter = React.useCallback(
    (r: Recipient) => {
      if (r.filterOnTab === 'mass' && !composeState.sendTypes.has('mass')) return false
      return true
    },
    [composeState.sendTypes]
  )

  const doubleClickAction = React.useMemo(
    () =>
      isTeacher
        ? {
            canDoubleClick: (recipient: Recipient) =>
              !!(recipient.group && (recipient.memberIDs || []).length < 50)
                ? { tooltip: 'Double click to expand' }
                : undefined,
            onDoubleClick: (recipient: Recipient) => {
              assert(recipient.group, 'Recipient must be a group to expand')
              return makeRequest<SingleStudentRecipient[]>('/frontend_api/fetchStudentsByIDs', {
                ids: recipient.memberIDs,
              }).then((students) => {
                Toasts.showToast({
                  message: (
                    <>
                      Expanded Group <b>{recipient.name}</b> into {students.length} students
                    </>
                  ),
                })
                return students
              })
            },
          }
        : undefined,
    [isTeacher]
  )

  const customSections = React.useMemo(
    () =>
      user.type === 'teacher' && user.makeCustomRecipient
        ? [
            {
              title: 'Select a group',
              body: (closeSection: () => void) => (
                <CustomRecipientMaker
                  schools={user.districtAccess ? user.district.schools : user.schools}
                  grades={user.makeCustomRecipient.grades}
                  addCustomRecipient={(recipient) => {
                    closeSection()
                    setSelectedRecipients((sr) => {
                      if (sr.some((r) => r.group && r.groupID === recipient.groupID)) return sr
                      return [...sr, recipient]
                    })
                  }}
                />
              ),
            },
          ]
        : [],
    [user]
  )

  const fetchUsersByIdentifiersByIdentifiers = React.useMemo(
    () => (isTeacher ? loadRecipientsByIdentifiers(user.type === 'teacher' ? 'student' : 'teacher') : undefined),
    [isTeacher]
  )

  const customSort = React.useMemo(
    () =>
      user.type === 'guardian'
        ? (a: Recipient, b: Recipient) => {
            if (!('foundByStudents' in a) || !('foundByStudents' in b)) return 0
            if (!a.foundByStudents || !b.foundByStudents || !composeState.regardingStudent) return 0
            return (
              (a.foundByStudents.includes(composeState.regardingStudent.id) ? -1 : 0) +
              (b.foundByStudents.includes(composeState.regardingStudent.id) ? 1 : 0)
            )
          }
        : undefined,
    [user, composeState.regardingStudent]
  )

  const recipientDescriptor = isTeacher
    ? composeState.sendTypes.has('mass')
      ? 'organizations and students'
      : 'students'
    : tr('teachers')

  return (
    <UserSelector<Recipient>
      id="tutorial_recipientSelector"
      className={`flex-grow`}
      url="/frontend_api/getRecipients"
      selectedUsers={composeState.selectedRecipients}
      setSelectedUsers={setSelectedRecipients}
      loadedUsers={loadedUsers}
      placeholder={tr(`Search for ${recipientDescriptor}`)}
      userDescriptor={recipientDescriptor}
      limitUsers={limitRecipients}
      invalid={highlightReasonsWeCantSend && composeState.selectedRecipients.length === 0}
      userVisibleFilter={userVisibleFilter}
      userDisplayConfig={userDisplayConfig}
      fetchUsersByIdentifiersForPaste={fetchUsersByIdentifiersByIdentifiers}
      doubleClickAction={doubleClickAction}
      customSections={customSections}
      customSort={customSort}
    />
  )
}

export const MassRecipientUpload: React.FC<{
  setLoading: (loading: boolean) => void
  setSelectedRecipients: React.Dispatch<React.SetStateAction<Recipient[]>>
}> = ({ setLoading, setSelectedRecipients }) => {
  return (
    <IconFileUpload
      className="mx-2 cursor-pointer opacity-25 hover:opacity-50"
      tooltip="Upload a file of recipients"
      onClick={async () => {
        if (
          !(await Popups.confirm({
            title: <b>Press `OK` to upload your recipients.</b>,
            message: (
              <>
                The file should be a csv with a column <b>'student_id'</b> or <b>'email'</b>, that we will use to find
                recipients.
              </>
            ),
            yesText: 'OK',
          }))
        )
          return
        const file = await inputSingleFile('text/csv')
        if (!file) {
          Toasts.showToast({
            message: 'Invalid file selected',
            error: true,
          })
          return
        }
        setLoading(true)
        const text = await file.text()
        const lines = text.split('\n')
        const header = lines[0].trim().split(',')
        const bestHeaderIndex = getBestHeaderIndex(header)

        let identifiers = []
        if (bestHeaderIndex !== undefined) {
          lines.splice(0, 1) // remove header
          identifiers = lines.map((l) => l.split(',')[bestHeaderIndex])
        } else {
          // assume there is no header, take all column 0
          identifiers = lines.map((l) => l.split(',')[0])
        }
        identifiers = identifiers
          .filter((i) => i !== undefined)
          .map((i) => i.replace(/(^"|"$)/g, '').trim())
          .filter((i) => i !== '')

        const recipients = await loadStudentsByIdentifiers()(identifiers)
        setLoading(false)
        if (recipients.length === 0) return
        const group = mergeStudentsIntoTempGroup(recipients)
        group.name += ` (csv)`
        setSelectedRecipients((sr) => [...sr, group])
      }}
    />
  )
}

const getBestHeaderIndex = (header: string[]) => {
  let bestHeaderIndex: number | undefined = undefined
  for (const headerTitle of ['student_id', 'student id', 'sync_id', 'id', 'email', 'student email', 'student_email']) {
    const matchIndex = header.findIndex(
      (h) =>
        h
          .toLowerCase()
          .replace(/(^"|"$)/g, '')
          .trim() === headerTitle
    )
    if (matchIndex !== -1) {
      bestHeaderIndex = matchIndex
      break
    }
  }
  return bestHeaderIndex
}

const mergeStudentsIntoTempGroup = (students: SingleStudentRecipient[]) => {
  return {
    group: true,
    groupID: 'custom-temp-' + students.map((r) => r.id).join(','),
    name: `${students.length} students`,
    contactable: {
      studentsWithoutReachableParents: students
        .filter((r) => r.contactable.selfHasNoReachableParents)
        .map((r) => r.name),
      unreachableStudents: students.filter((r) => r.contactable.unreachableSelf).map((r) => r.name),
      studentsWithoutReachableParentsMass: students
        .filter((r) => r.contactable.selfHasNoReachableParentsMass)
        .map((r) => r.name),
      unreachableStudentsMass: students.filter((r) => r.contactable.unreachableSelfMass).map((r) => r.name),
    },
    memberIDs: students.map((s) => s.id),
    guardianIDs: [...new Set(students.flatMap((s) => s.guardians.map((g) => g.id)))],
    filterOnTab: 'mass',
  } as StudentGroupRecipient
}
